/**
 * System Integration Test - COMPLETE SYSTEM VALIDATION
 *
 * Comprehensive test suite that validates ALL major systems work together.
 * Run this before engineering handoff to ensure system integrity.
 *
 * Usage: In browser console, run: testSystem()
 */

import { allObjectFields } from '../services/mockData/objectManagerFields';

interface TestResult {
  category: string;
  test: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
  details?: any;
  duration?: number;
}

// Object API name to Object ID mapping
const objectApiToId: Record<string, string> = {
  'Lead': 'obj_lead',
  'Client': 'obj_client',
  'Contact': 'obj_contact',
  'Opportunity': 'obj_opportunity',
  'Case': 'obj_case',
  'Task': 'obj_task',
  'Event': 'obj_event',
  'Report': 'obj_report',
  'User': 'obj_user'
};

// Storage key mapping
const objectStorageKeys: Record<string, string> = {
  'Lead': 'crm_lead_records',
  'Client': 'crm_client_records',
  'Contact': 'crm_contact_records',
  'Opportunity': 'crm_opportunity_records',
  'Case': 'crm_case_records',
  'Task': 'crm_task_records',
  'Event': 'crm_event_records',
  'Report': 'crm_report_records'
};

export class SystemIntegrationTest {
  private results: TestResult[] = [];
  private startTime: number = 0;

  /**
   * Run all integration tests
   */
  public async runAllTests(): Promise<TestResult[]> {
    console.log('🧪 COMPREHENSIVE SYSTEM INTEGRATION TEST\n');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('Testing ALL implemented features...\n');
    console.log('═══════════════════════════════════════════════════════════\n');

    this.results = [];
    this.startTime = Date.now();

    // OBJECT MANAGER TESTS
    await this.testObjectManagerCore();
    await this.testListViews();

    // WORKFLOW RULES TESTS
    await this.testWorkflowRules();

    // DATA MANAGEMENT TESTS
    await this.testRecordOperations();

    // CROSS-SYSTEM TESTS
    await this.testCrossSystemIntegration();
    await this.testDataConsistency();

    // Print results
    this.printResults();

    return this.results;
  }

  // ═══════════════════════════════════════════════════════════
  // OBJECT MANAGER TESTS
  // ═══════════════════════════════════════════════════════════

  /**
   * Test 1: Object Manager Core
   */
  private async testObjectManagerCore(): Promise<void> {
    console.log('📋 TEST 1: Object Manager - Core Objects & Fields\n');

    const objects = ['Lead', 'Client', 'Contact', 'Opportunity', 'Case', 'Task', 'Event', 'Report'];
    const systemFields = ['id', 'createdDate', 'createdBy', 'lastModifiedDate', 'lastModifiedBy', 'owner'];

    for (const objectName of objects) {
      const start = Date.now();
      const objectId = objectApiToId[objectName];
      const fields = allObjectFields[objectId];

      if (!fields) {
        this.addResult('Object Manager Core', `${objectName} exists`, 'fail',
          `Object ${objectName} not found`, null, Date.now() - start);
        continue;
      }

      // Check system fields
      const missingFields = systemFields.filter(sf =>
        !fields.some(f => f.fieldName === sf && f.isSystemField)
      );

      if (missingFields.length > 0) {
        this.addResult('Object Manager Core', `${objectName} system fields`, 'fail',
          `Missing system fields: ${missingFields.join(', ')}`,
          { missingFields }, Date.now() - start);
      } else {
        this.addResult('Object Manager Core', `${objectName} system fields`, 'pass',
          `All 6 system fields present`, null, Date.now() - start);
      }

      // Check has custom fields
      const customFields = fields.filter(f => !f.isSystemField);
      if (customFields.length === 0) {
        this.addResult('Object Manager Core', `${objectName} custom fields`, 'warning',
          'No custom fields defined', null, Date.now() - start);
      } else {
        this.addResult('Object Manager Core', `${objectName} custom fields`, 'pass',
          `${customFields.length} custom fields defined`, null, Date.now() - start);
      }
    }
  }

  /**
   * Test 2: List Views
   */
  private async testListViews(): Promise<void> {
    console.log('\n📊 TEST 2: Object Manager - List Views\n');

    const objects = ['Lead', 'Client', 'Contact', 'Opportunity', 'Case', 'Task', 'Event'];

    for (const objectName of objects) {
      const start = Date.now();
      const storageKey = `crm_${objectName.toLowerCase()}_list_views`;
      const listViewsData = localStorage.getItem(storageKey);

      if (!listViewsData) {
        this.addResult('List Views', `${objectName} list views`, 'warning',
          `No cached list views for ${objectName}`, null, Date.now() - start);
        continue;
      }

      const views = JSON.parse(listViewsData);
      const objectId = objectApiToId[objectName];
      const objectFields = allObjectFields[objectId];

      if (!objectFields) continue;

      let invalidColumns = 0;
      views.forEach((view: any) => {
        view.columns?.forEach((column: any) => {
          const field = objectFields.find(f => f.fieldName === column.fieldApiName);
          if (!field) {
            invalidColumns++;
            this.addResult('List Views', `${objectName}.${column.fieldApiName}`, 'fail',
              `Column not found in Object Manager`,
              { view: view.name, column: column.fieldApiName }, Date.now() - start);
          }
        });
      });

      if (invalidColumns === 0) {
        this.addResult('List Views', `${objectName} views`, 'pass',
          `${views.length} view(s), all columns valid`, null, Date.now() - start);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════
  // WORKFLOW RULES TESTS
  // ═══════════════════════════════════════════════════════════

  /**
   * Test 3: Workflow Rules Configuration
   */
  private async testWorkflowRules(): Promise<void> {
    console.log('\n⚡ TEST 3: Workflow Rules - Configuration\n');

    const start = Date.now();
    const workflowsData = localStorage.getItem('crm_workflow_rules');

    if (!workflowsData) {
      this.addResult('Workflow Rules', 'Rules exist', 'warning',
        'No workflow rules defined', null, Date.now() - start);
      return;
    }

    const workflows = JSON.parse(workflowsData);
    this.addResult('Workflow Rules', 'Rules loaded', 'pass',
      `${workflows.length} workflow rule(s) found`, null, Date.now() - start);

    // Test each rule
    for (const workflow of workflows) {
      const ruleStart = Date.now();

      // Check has objectApiName
      if (!workflow.objectApiName) {
        this.addResult('Workflow Rules', `${workflow.name}`, 'fail',
          'Missing objectApiName - CRITICAL', { ruleId: workflow.id }, Date.now() - ruleStart);
        continue;
      }

      // Check object exists
      const objectId = objectApiToId[workflow.objectApiName];
      const objectFields = allObjectFields[objectId];

      if (!objectFields) {
        this.addResult('Workflow Rules', `${workflow.name}`, 'fail',
          `Object ${workflow.objectApiName} not found in Object Manager`,
          { ruleId: workflow.id }, Date.now() - ruleStart);
        continue;
      }

      // Check criteria fields exist
      let invalidFields = 0;
      if (workflow.evaluationCriteria?.conditions) {
        for (const condition of workflow.evaluationCriteria.conditions) {
          const field = objectFields.find(f => f.fieldName === condition.fieldApiName);
          if (!field) {
            invalidFields++;
            this.addResult('Workflow Rules', `${workflow.name} - criteria`, 'fail',
              `Field ${condition.fieldApiName} not found in ${workflow.objectApiName}`,
              { ruleId: workflow.id, field: condition.fieldApiName }, Date.now() - ruleStart);
          }
        }
      }

      // Check action fields exist
      if (workflow.actions) {
        for (const action of workflow.actions) {
          if (action.type === 'fieldUpdate') {
            const field = objectFields.find(f => f.fieldName === action.config.fieldApiName);
            if (!field) {
              invalidFields++;
              this.addResult('Workflow Rules', `${workflow.name} - action`, 'fail',
                `Field ${action.config.fieldApiName} not found in ${workflow.objectApiName}`,
                { ruleId: workflow.id, field: action.config.fieldApiName }, Date.now() - ruleStart);
            }
          }
        }
      }

      if (invalidFields === 0) {
        this.addResult('Workflow Rules', `${workflow.name}`, 'pass',
          'Rule configuration fully valid',
          { active: workflow.isActive, trigger: workflow.triggerType }, Date.now() - ruleStart);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════
  // RECORD OPERATIONS TESTS
  // ═══════════════════════════════════════════════════════════

  /**
   * Test 4: Record CRUD Operations
   */
  private async testRecordOperations(): Promise<void> {
    console.log('\n💾 TEST 4: Record CRUD Operations\n');

    const objects = ['Lead', 'Client', 'Contact', 'Opportunity', 'Case', 'Task', 'Event', 'Report'];

    for (const objectName of objects) {
      const start = Date.now();
      const storageKey = objectStorageKeys[objectName];
      const recordsData = localStorage.getItem(storageKey);

      if (!recordsData) {
        this.addResult('Record Operations', `${objectName} records`, 'warning',
          `No ${objectName} records found`, null, Date.now() - start);
        continue;
      }

      const records = JSON.parse(recordsData);

      if (records.length === 0) {
        this.addResult('Record Operations', `${objectName} records`, 'warning',
          `No ${objectName} records found`, null, Date.now() - start);
        continue;
      }

      // Check first record has system fields
      const record = records[0];
      const systemFields = ['id', 'createdDate', 'owner'];
      const missingFields = systemFields.filter(sf => !record[sf]);

      if (missingFields.length > 0) {
        this.addResult('Record Operations', `${objectName} system fields`, 'warning',
          `Records missing: ${missingFields.join(', ')}`,
          { storageKey, missingFields }, Date.now() - start);
      } else {
        this.addResult('Record Operations', `${objectName} records`, 'pass',
          `${records.length} record(s) with valid system fields`,
          null, Date.now() - start);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════
  // CROSS-SYSTEM TESTS
  // ═══════════════════════════════════════════════════════════

  /**
   * Test 5: Cross-System Integration
   */
  private async testCrossSystemIntegration(): Promise<void> {
    console.log('\n🔗 TEST 5: Cross-System Integration\n');

    const start = Date.now();

    // Test: All list views reference valid fields
    const objects = ['Lead', 'Client', 'Contact', 'Opportunity', 'Case', 'Task', 'Event'];
    let allValid = true;

    for (const objectName of objects) {
      const storageKey = `crm_${objectName.toLowerCase()}_list_views`;
      const listViewsData = localStorage.getItem(storageKey);

      if (!listViewsData) continue;

      const views = JSON.parse(listViewsData);
      const objectId = objectApiToId[objectName];
      const objectFields = allObjectFields[objectId];

      if (!objectFields) continue;

      for (const view of views) {
        for (const column of view.columns || []) {
          const field = objectFields.find(f => f.fieldName === column.fieldApiName);
          if (!field) {
            allValid = false;
          }
        }
      }
    }

    if (allValid) {
      this.addResult('Cross-System', 'List Views → Object Manager', 'pass',
        'All list view columns reference valid Object Manager fields', null, Date.now() - start);
    } else {
      this.addResult('Cross-System', 'List Views → Object Manager', 'fail',
        'Some list view columns reference invalid fields', null, Date.now() - start);
    }

    // Test: All workflow rules reference valid objects
    const workflowsData = localStorage.getItem('crm_workflow_rules');
    if (workflowsData) {
      const workflows = JSON.parse(workflowsData);
      let allWorkflowsValid = true;

      for (const workflow of workflows) {
        if (!workflow.objectApiName || !objectApiToId[workflow.objectApiName]) {
          allWorkflowsValid = false;
        }
      }

      if (allWorkflowsValid) {
        this.addResult('Cross-System', 'Workflows → Object Manager', 'pass',
          'All workflows reference valid objects', null, Date.now() - start);
      } else {
        this.addResult('Cross-System', 'Workflows → Object Manager', 'fail',
          'Some workflows reference invalid objects', null, Date.now() - start);
      }
    }
  }

  /**
   * Test 6: Data Consistency
   */
  private async testDataConsistency(): Promise<void> {
    console.log('\n✓ TEST 6: Data Consistency\n');

    const start = Date.now();

    // Check for duplicate IDs in records
    const objects = [
      { name: 'Lead', key: 'crm_lead_records' },
      { name: 'Client', key: 'crm_client_records' },
      { name: 'Contact', key: 'crm_contact_records' },
      { name: 'Opportunity', key: 'crm_opportunity_records' },
      { name: 'Case', key: 'crm_case_records' },
      { name: 'Task', key: 'crm_task_records' },
      { name: 'Event', key: 'crm_event_records' },
      { name: 'Report', key: 'crm_report_records' }
    ];

    let hasDuplicates = false;
    for (const obj of objects) {
      const recordsData = localStorage.getItem(obj.key);
      if (!recordsData) continue;

      const records = JSON.parse(recordsData);
      const ids = records.map((r: any) => r.id);
      const uniqueIds = new Set(ids);

      if (ids.length !== uniqueIds.size) {
        hasDuplicates = true;
        this.addResult('Data Consistency', `${obj.name} duplicate IDs`, 'fail',
          `Duplicate IDs found in ${obj.name} records`,
          { storageKey: obj.key }, Date.now() - start);
      }
    }

    if (!hasDuplicates) {
      this.addResult('Data Consistency', 'No duplicate IDs', 'pass',
        'All records have unique IDs', null, Date.now() - start);
    }

    // Check workflow rules reference valid objects
    const workflowsData = localStorage.getItem('crm_workflow_rules');
    if (workflowsData) {
      const workflows = JSON.parse(workflowsData);
      let orphanedRules = 0;

      for (const workflow of workflows) {
        const objectId = objectApiToId[workflow.objectApiName];
        if (!objectId || !allObjectFields[objectId]) {
          orphanedRules++;
        }
      }

      if (orphanedRules === 0) {
        this.addResult('Data Consistency', 'No orphaned workflows', 'pass',
          'All workflows reference valid objects', null, Date.now() - start);
      } else {
        this.addResult('Data Consistency', 'Orphaned workflows', 'fail',
          `${orphanedRules} workflow(s) reference invalid objects`,
          { orphanedCount: orphanedRules }, Date.now() - start);
      }
    }
  }

  // ═══════════════════════════════════════════════════════════
  // UTILITY METHODS
  // ═══════════════════════════════════════════════════════════

  /**
   * Add test result
   */
  private addResult(
    category: string,
    test: string,
    status: 'pass' | 'fail' | 'warning',
    message: string,
    details?: any,
    duration?: number
  ): void {
    this.results.push({ category, test, status, message, details, duration });
  }

  /**
   * Print results
   */
  private printResults(): void {
    const totalTime = Date.now() - this.startTime;

    console.log('\n═══════════════════════════════════════════════════════════');
    console.log('SYSTEM INTEGRATION TEST RESULTS');
    console.log('═══════════════════════════════════════════════════════════\n');

    const byCategory = this.groupBy(this.results, 'category');

    Object.entries(byCategory).forEach(([category, results]: [string, TestResult[]]) => {
      console.log(`\n📋 ${category}`);
      console.log('─'.repeat(60));

      results.forEach(result => {
        const icon = result.status === 'pass' ? '✅' : result.status === 'fail' ? '❌' : '⚠️';
        const duration = result.duration ? ` (${result.duration}ms)` : '';
        console.log(`${icon} ${result.test}${duration}`);
        console.log(`   ${result.message}`);
        if (result.details) {
          console.log(`   Details:`, result.details);
        }
      });
    });

    // Summary
    const total = this.results.length;
    const passed = this.results.filter(r => r.status === 'pass').length;
    const failed = this.results.filter(r => r.status === 'fail').length;
    const warnings = this.results.filter(r => r.status === 'warning').length;

    console.log('\n═══════════════════════════════════════════════════════════');
    console.log('SUMMARY');
    console.log('═══════════════════════════════════════════════════════════');
    console.log(`Total Tests: ${total}`);
    console.log(`✅ Passed: ${passed} (${Math.round(passed/total*100)}%)`);
    console.log(`❌ Failed: ${failed} (${Math.round(failed/total*100)}%)`);
    console.log(`⚠️  Warnings: ${warnings} (${Math.round(warnings/total*100)}%)`);
    console.log(`⏱️  Total Time: ${totalTime}ms`);

    if (failed === 0) {
      console.log('\n🎉 SYSTEM INTEGRATION TEST PASSED!');
      console.log('✅ All features working correctly!');
      console.log('✅ System is ready for engineering handoff!');
    } else {
      console.log('\n🚨 SYSTEM INTEGRATION TEST FAILED!');
      console.log(`❌ ${failed} critical errors found`);
      console.log('❌ Fix errors before engineering handoff!');
    }

    console.log('═══════════════════════════════════════════════════════════\n');
  }

  /**
   * Group results by category
   */
  private groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
    return array.reduce((result, item) => {
      const group = String(item[key]);
      if (!result[group]) result[group] = [];
      result[group].push(item);
      return result;
    }, {} as Record<string, T[]>);
  }
}

// Export singleton
export const systemIntegrationTest = new SystemIntegrationTest();

// Browser console helper
if (typeof window !== 'undefined') {
  (window as any).testSystem = () => systemIntegrationTest.runAllTests();
}
